// Dawan Application Bootstrap
import '../css/app.css';
import './bootstrap';

import { createInertiaApp, router } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import { createApp, h } from 'vue';
import { ZiggyVue } from '../../vendor/tightenco/ziggy';
import pinia from './plugins/pinia';
import { setActivePinia } from 'pinia';
import vuetify from './plugins/vuetify';
import VueApexCharts from "vue3-apexcharts";

const appName = import.meta.env.VITE_APP_NAME || 'Dawan';

const getInitialPage = () => {
    // 1. Inertia script tag format (Inertia v2 / v3)
    const script = document.querySelector('script[data-page="app"][type="application/json"]');
    if (script && script.textContent) {
        try {
            return JSON.parse(script.textContent);
        } catch (e) {
            console.error('Inertia: Error parsing script data-page JSON', e);
        }
    }

    // 2. Legacy Inertia root element dataset format (Inertia v1)
    const appEl = document.getElementById('app');
    if (appEl && appEl.dataset && appEl.dataset.page) {
        try {
            return JSON.parse(appEl.dataset.page);
        } catch (e) {
            console.error('Inertia: Error parsing element data-page attribute', e);
        }
    }

    return null;
};

const initInertia = () => {
    const page = getInitialPage();

    if (!page) {
        console.warn('Inertia: No initial page data found in DOM. Skipping Inertia mounting.');
        return;
    }

    createInertiaApp({
        page,
        title: (title) => `${title} - ${appName}`,
        resolve: (name) => {
            const pages = import.meta.glob('./Pages/**/*.vue');
            let pageComponent = pages[`./Pages/${name}.vue`];
            
            if (!pageComponent) {
                const searchKey = `./Pages/${name}.vue`.toLowerCase();
                const matchedKey = Object.keys(pages).find(key => key.toLowerCase() === searchKey);
                if (matchedKey) {
                    pageComponent = pages[matchedKey];
                }
            }
            
            if (!pageComponent) {
                throw new Error(`Page not found: ./Pages/${name}.vue`);
            }
            
            return typeof pageComponent === 'function' ? pageComponent() : pageComponent;
        },
        setup({ el, App, props, plugin }) {
            const mountEl = el || document.getElementById('app');
            if (!mountEl) {
                console.error('Inertia: Target element #app not found.');
                return;
            }

            setActivePinia(pinia);

            const ziggyConfig = props?.initialPage?.props?.ziggy;
            if (ziggyConfig && typeof window !== 'undefined') {
                window.Ziggy = ziggyConfig;
            }

            const appInstance = createApp({ render: () => h(App, props) })
                .use(pinia)
                .use(plugin)
                .use(ZiggyVue, ziggyConfig)
                .use(vuetify)
                .use(VueApexCharts)
                .mount(mountEl);

            router.on('navigate', (event) => {
                if (event.detail?.page?.props?.ziggy && typeof window !== 'undefined') {
                    window.Ziggy = event.detail.page.props.ziggy;
                }
            });

            if (typeof window !== 'undefined' && typeof window.dismissPwaSplash === 'function') {
                requestAnimationFrame(() => {
                    window.dismissPwaSplash();
                });
            }

            return appInstance;
        },
        progress: {
            color: '#4B5563',
        },
    });
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initInertia);
} else {
    initInertia();
}
